/* Vista Tenant Layout — wide horizons, airy, magazine-cover energy.

   What makes this different from Editorial:
   • Ultra-thin sticky header rail at the top — barely there — so the
     content has room to breathe. The studio name sits left in widely
     tracked uppercase Playfair, nav lives right with breadcrumb chevrons
     between items (Home › Portfolio › Services › Contact).
   • A single hairline seafoam rule across the full viewport width
     beneath the header — symbolizing the horizon.
   • Geographic coordinates rendered as a tiny caption in the bottom-left
     corner of every page (e.g. "40.7128° N · 74.0060° W") when the
     studio has a contact_location set — postcard-stamp energy.
   • Footer is a single horizontal band: postal-stamp Vista wordmark on
     the left, contact info center, social on right, all on one line.
*/

import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useTenantTheme } from "@/hooks/useTenantTheme";
import { tenantHref } from "@/lib/tenant";
import TenantSignature from "@/components/tenant/TenantSignature";
import LumetierFooterCredit from "@/components/tenant/LumetierFooterCredit";
import TenantMobileNav from "@/components/tenant/TenantMobileNav";
import TenantWordmark from "@/components/tenant/TenantWordmark";
import { ChevronRight, Instagram, Mail, MapPin } from "lucide-react";

const NAV = [
  { to: "/",          label: "Home",      end: true },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/services",  label: "Services"  },
  { to: "/contact",   label: "Contact"   },
];

const DISPLAY = "'Playfair Display', 'Cormorant Garamond', serif";

export default function VistaTenantLayout() {
  useTenantTheme();
  const { settings } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const year = new Date().getFullYear();

  return (
    <div
      className="min-h-screen antialiased relative"
      style={{
        fontFamily: "var(--tenant-sans)",
        background: "var(--tenant-bg, #F0F4F4)",
        color: "var(--tenant-fg, #1C2D2D)",
      }}
      data-template="vista"
    >
      {/* ── Ultra-thin horizon header ─────────────────────────────────── */}
      <header
        className={`fixed top-0 inset-x-0 z-40 transition-all ${scrolled ? "h-10 backdrop-blur-md" : "h-12"}`}
        style={{
          background: scrolled
            ? "color-mix(in srgb, var(--tenant-bg, #F0F4F4) 92%, transparent)"
            : "color-mix(in srgb, var(--tenant-bg, #F0F4F4) 60%, transparent)",
        }}
        data-testid="vista-tenant-nav"
      >
        <div className="max-w-[1480px] mx-auto px-6 lg:px-12 h-full flex items-center justify-between gap-6">
          <Link
            to={tenantHref("/")}
            className="text-[11px] uppercase tracking-[0.42em] hover:opacity-70 transition-opacity"
            style={{ color: "var(--tenant-fg)", fontFamily: DISPLAY }}
            data-testid="vista-tenant-wordmark"
          >
            <TenantWordmark logoUrl={settings?.logo_url} coverLogoUrl={settings?.cover_logo_url} tone="light" studioName={studioName} imgClassName="h-6 w-auto">
              {studioName}
            </TenantWordmark>
          </Link>
          {/* Breadcrumb-style nav with chevrons between */}
          <nav className="hidden md:flex items-center" aria-label="Primary">
            {NAV.map((it, i) => (
              <span key={it.to} className="flex items-center">
                <NavLink
                  to={tenantHref(it.to)}
                  end={it.end}
                  data-testid={`vista-tenant-nav-${it.label.toLowerCase()}`}
                  className="text-[10px] uppercase tracking-[0.32em] transition-colors px-2"
                  style={({ isActive }) => ({
                    color: isActive ? "var(--tenant-accent)" : "var(--tenant-fg-soft, rgba(28,45,45,0.55))",
                  })}
                >
                  {it.label}
                </NavLink>
                {i < NAV.length - 1 && (
                  <ChevronRight className="w-3 h-3 opacity-40" />
                )}
              </span>
            ))}
            <Link
              to={tenantHref("/booking")}
              className="ml-3 inline-flex items-center gap-1.5 px-4 h-7 text-[10px] uppercase tracking-[0.32em] border transition-colors hover:opacity-90"
              style={{ borderColor: "var(--tenant-accent)", color: "var(--tenant-accent)" }}
              data-testid="vista-tenant-book-cta"
            >
              Book the Field
            </Link>
          </nav>
          {/* Mobile-only hamburger — sits at the right end of the ultra-thin
              horizon rail, replacing the desktop nav strip on phones. */}
          <TenantMobileNav tone="light" ctaLabel="Book the field" studioName={studioName} />
        </div>
        {/* Horizon line */}
        <div
          className="absolute bottom-0 inset-x-0 h-px"
          style={{ background: "var(--tenant-accent)", opacity: 0.5 }}
        />
      </header>

      {/* ── Body ──────────────────────────────────────────────────────── */}
      <main key={location.pathname} className="pt-12">
        <Outlet />
      </main>

      {/* ── Geographic stamp (bottom-left, fixed, desktop only) ───────── */}
      {settings?.contact_location && (
        <div
          className="hidden lg:flex items-center gap-1.5 fixed bottom-6 left-6 z-20 text-[9px] uppercase tracking-[0.42em]"
          style={{ color: "var(--tenant-fg-mute, rgba(28,45,45,0.40))" }}
          aria-hidden
        >
          <MapPin className="w-3 h-3" strokeWidth={1.5} />
          <span>{settings.contact_location}</span>
        </div>
      )}

      {/* ── Single-band horizontal footer ─────────────────────────────── */}
      <footer
        className="border-t mt-16"
        style={{
          background: "var(--tenant-bg, #F0F4F4)",
          borderTopColor: "var(--tenant-accent)",
        }}
        data-testid="vista-tenant-footer"
      >
        <div className="max-w-[1480px] mx-auto px-6 lg:px-12 py-8 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="text-2xl lg:text-3xl tracking-tight" style={{ fontFamily: DISPLAY, color: "var(--tenant-fg)" }}>
              {studioName}
            </div>
            {settings?.tagline && (
              <p className="text-xs mt-1 italic max-w-sm" style={{ color: "var(--tenant-fg-soft)" }}>
                {settings.tagline}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-6 text-[11px] uppercase tracking-[0.32em]" style={{ color: "var(--tenant-fg-soft)" }}>
            {settings?.contact_location && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="w-3 h-3" strokeWidth={1.5} style={{ color: "var(--tenant-accent)" }} />
                {settings.contact_location}
              </span>
            )}
            {settings?.contact_email && (
              <a href={`mailto:${settings.contact_email}`} className="inline-flex items-center gap-1.5 hover:opacity-100 normal-case tracking-normal" style={{ opacity: 0.85 }}>
                <Mail className="w-3 h-3" strokeWidth={1.5} style={{ color: "var(--tenant-accent)" }} />
                {settings.contact_email}
              </a>
            )}
            {settings?.instagram_url && (
              <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" className="hover:opacity-100" style={{ opacity: 0.85, color: "var(--tenant-accent)" }}>
                <Instagram className="w-4 h-4" strokeWidth={1.5} />
              </a>
            )}
          </div>
        </div>
        <div className="max-w-[1480px] mx-auto px-6 lg:px-12 pb-6 border-t pt-4 text-[10px] uppercase tracking-[0.42em] flex items-center justify-between"
             style={{ borderTopColor: "var(--tenant-fg-line)", color: "var(--tenant-fg-mute)" }}>
          <span>© {year} {studioName}</span>
          <LumetierFooterCredit className="hover:opacity-100" testid="vista-lumetier-credit" />
          <span style={{ color: "var(--tenant-accent)" }}>Field Notes</span>
        </div>
      </footer>

      <TenantSignature tone="light" />
    </div>
  );
}
